import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../hooks/useIsMobile.js";
import { api } from "../config/api.js";
import { DEMO_ID } from "../utils/demo.js";
import { AddonPicker } from "../components/AddonPicker.jsx";
import { activeAddons, resolveAddons, addonsTotal } from "../utils/addons.js";
import { today, effectiveShipping, bundleDiscount, bogoDiscount, bogoConfig, bogoLabel, bogoFreeCount, bogoUnitsToNext } from "../utils/helpers.js";
import { findCoupon } from "../utils/coupons.js";
import { reconcileCart, noticeText } from "../utils/cartIntegrity.js";
import { quote, feeFlags } from "../utils/fees.js";
import { useMopConfig, methodOpts } from "../hooks/useMopConfig.js";
import { Icon, BloomMark } from "../components/Icon.jsx";
import { UpiCheckoutModal } from "../components/UpiCheckoutModal.jsx";
import { startRazorpayPayment, loadRazorpaySDK, isHostedPay, startHostedRazorpayCheckout } from "../utils/razorpay.js";
import { sendOrderEmail } from "../utils/email.js";
import { thumbUrl } from "../utils/imageUpload.js";
import { AddressModal } from "./Storefront.jsx";
import { ThemeWrapper } from "../components/ThemeWrapper.jsx";
import { BACKEND_URL } from "../config/supabase.js";

// ─── CART ─────────────────────────────────────────────────────────────────────
export function Cart({ cart, setCart, clearCart, storeUser: storeUserProp, onOrderPlaced, customer, paymentsPaused = false }) {
  const isMobile  = useIsMobile();
  const navigate  = useNavigate();
  // Preload Razorpay SDK + pre-warm Render backend on mount so there's no cold-start
  // delay when customer actually taps Pay (Render free/standard sleeps after inactivity)
  useEffect(() => {
    loadRazorpaySDK().catch(()=>{});
    fetch(`${BACKEND_URL}/api/health`).catch(()=>{}); // fire-and-forget keepalive
  }, []);

  const pendingOrderIdsRef = useRef([]);
  const enrichedCartRef    = useRef([]);
  const lastPayArgsRef     = useRef(null);   // address+contact, to re-pay after removing sold-out items
  const retryAfterCartChange = useRef(false); // re-run handlePay once the trimmed cart state lands

  // NOTE: we deliberately do NOT release the hold on tab/browser close. On UPI the
  // customer often closes the page and then completes payment in their banking app —
  // releasing here would free the item, let someone else buy it, and force a refund
  // when the payment lands. The hold instead self-expires (30-min reserve TTL) and the
  // backend stale-cron cancels truly-abandoned orders. A genuine payment.failed (or the
  // user starting a fresh checkout) is what releases the hold.

  const [paying,        setPaying]        = useState(false);
  const [payStep,       setPayStep]       = useState("connecting"); // connecting | modal | verifying
  const [showAddr,      setShowAddr]      = useState(false);
  const [intlCountry,   setIntlCountry]   = useState(null);   // delivery country (set by AddressModal); non-India = international
  const [deliveryState, setDeliveryState] = useState(null);   // India delivery state → per-state shipping
  const [note,          setNote]          = useState("");
  const [showBreakdown, setShowBreakdown] = useState(false); // fee breakdown collapsed by default
  // MOP (Mode-of-Payment): UPI pre-selected (93% of real orders — most customers change
  // nothing). `mop.enabled` gates the whole feature off (admin switch OR ?mop=1 preview) —
  // when disabled, activeMethod is null and quote()/checkout behave exactly like before MOP.
  const [payMethod,     setPayMethod]     = useState("upi");
  const mop = useMopConfig();
  // Single-store coupon state
  const [couponCode,    setCouponCode]    = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponError,   setCouponError]   = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  // Multi-store per-store coupon state: { [storeId]: { code, applied, error, loading } }
  const [storeCoupons,  setStoreCoupons]  = useState({});
  // storeInfo fetch error state — if fetch fails after timeout, show retry instead of blocking forever
  const [storeInfoFetchFailed, setStoreInfoFetchFailed] = useState(false);
  // Sold-out flow: checkout returns the full list of items it couldn't reserve; we show
  // it and let the customer remove those items and continue. { list, retryable, message }
  const [soldOut, setSoldOut] = useState(null);
  // Checkout add-ons the shopper ticked: { [storeId]: [addonId, …] }. Resolved against each store's LIVE add-on list, so a removed add-on
  // simply stops counting.
  const [addonSel, setAddonSel] = useState({});
  const toggleAddon = (sid, id) => setAddonSel(prev => { const cur = new Set((prev[sid] || []).map(String)); cur.has(String(id)) ? cur.delete(String(id)) : cur.add(String(id)); return { ...prev, [sid]: [...cur] }; });

  // ── Auto-enrich items with _storeInfo, and REFRESH it for every store in the cart ──
  // An item's own `_storeInfo` is a snapshot from when it was added to the cart. If the
  // seller changes shipping_cost, a free-ship threshold, a bundle/BOGO offer, or a coupon
  // AFTER that, a cart holding the old snapshot would silently keep pricing by the stale
  // numbers — for a multi-store cart this was the "follow-up open" gap left by the
  // free_shipping self-heal below, which only patches one per-product field, not the whole
  // per-store object. Fetch bulk store info for EVERY store represented in the cart (not
  // just ones missing it) once per page load, and let enrichedCart prefer the fresh copy.
  const [storeInfoCache, setStoreInfoCache] = useState({});
  const fetchedStoreInfoRef = useRef(new Set());
  useEffect(() => {
    const cartStoreIds = [...new Set(
      cart.map(i => i.user_id || i._storeInfo?.id).filter(Boolean)
    )];
    const toFetch = cartStoreIds.filter(id => !fetchedStoreInfoRef.current.has(id));
    if (!toFetch.length) return;
    toFetch.forEach(id => fetchedStoreInfoRef.current.add(id));
    setStoreInfoFetchFailed(false);
    // 6-second timeout — if fetch hangs (Render cold start, network blip), mark as
    // failed so user gets a retry button instead of being permanently blocked at checkout.
    const timeout = setTimeout(() => setStoreInfoFetchFailed(true), 6000);
    fetch(`${BACKEND_URL}/api/store-info-bulk?ids=${toFetch.join(",")}`)
      .then(r => r.json())
      .then(data => {
        clearTimeout(timeout);
        if (!data.stores) { setStoreInfoFetchFailed(true); toFetch.forEach(id => fetchedStoreInfoRef.current.delete(id)); return; }
        const map = {};
        data.stores.forEach(s => { map[s.id] = s; });
        setStoreInfoCache(prev => ({ ...prev, ...map }));
        setStoreInfoFetchFailed(false);
      })
      .catch(() => { clearTimeout(timeout); setStoreInfoFetchFailed(true); toFetch.forEach(id => fetchedStoreInfoRef.current.delete(id)); });
  }, [cart]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Refresh each item's free_shipping from the store's CURRENT products ───────
  // A cart item keeps the free_shipping value from WHEN it was added. If the seller
  // later turns free shipping OFF, a stale cart would still show ₹0 shipping and
  // under-charge at checkout (the backend now guards this, but the buyer should see the
  // correct total UPFRONT rather than get bounced). Re-fetch the live per-store flag and
  // let it override the snapshot. Falls back to the stored value on any fetch failure.
  const [freshFreeShip, setFreshFreeShip] = useState({});   // productId → current free_shipping
  const [freshStock,    setFreshStock]    = useState({});   // productId → { stock, variations, hasAdvanced } LIVE (a cart item's own stock is only a snapshot from when it was added)
  const fetchedFreshRef = useRef(new Set());
  useEffect(() => {
    const storeIds = [...new Set(cart.map(i => i.user_id || i._storeInfo?.id || storeUserProp?.id).filter(Boolean))];
    const toFetch = storeIds.filter(sid => !fetchedFreshRef.current.has(sid));
    if (!toFetch.length) return;
    toFetch.forEach(async sid => {
      fetchedFreshRef.current.add(sid);
      try {
        const r = await fetch(`${BACKEND_URL}/api/store/${encodeURIComponent(sid)}/products`);
        if (!r.ok) { fetchedFreshRef.current.delete(sid); return; }   // allow a later retry
        const rows = await r.json();
        const list = Array.isArray(rows) ? rows : (rows.products || []);
        if (!list.length) return;
        const map = {};
        const stockMap = {};
        list.forEach(p => { if (p && p.id != null) { map[p.id] = p.free_shipping === true; stockMap[p.id] = { stock: p.stock, variations: p.variations, price: p.price, hasAdvanced: !!(p.advanced_variations && Object.keys(p.advanced_variations).length), advContent: !!(p.advanced_variations && (['colour','size'].some(k => Array.isArray(p.advanced_variations[k]) && p.advanced_variations[k].length) || (p.advanced_variations.custom && Array.isArray(p.advanced_variations.custom.values) && p.advanced_variations.custom.values.length))) }; } });
        setFreshFreeShip(prev => ({ ...prev, ...map }));
        setFreshStock(prev => ({ ...prev, ...stockMap }));
      } catch { fetchedFreshRef.current.delete(sid); }                // network blip → allow retry
    });
  }, [cart, storeUserProp]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build enriched cart: inject _storeInfo, PREFERRING the freshly-fetched copy over the
  // item's own (possibly stale) snapshot — fixes shipping_cost/thresholds/bundle/BOGO/coupon
  // staleness. Falls back to the item's embedded snapshot only while the fresh fetch is still
  // in flight or failed, so the cart never goes blank mid-load or on a network blip. Also
  // overrides free_shipping with the freshly-fetched live per-product flag, as before.
  const enrichedCart = useMemo(() => cart.map(item => {
    const fresh = (item.id != null && item.id in freshFreeShip) ? { free_shipping: freshFreeShip[item.id] } : null;
    const sid  = item.user_id || item._storeInfo?.id;
    const info = (sid && storeInfoCache[sid]) || item._storeInfo;
    if (!info) return fresh ? { ...item, ...fresh } : item;
    return { ...item, _storeInfo: info, ...(fresh || {}) };
  }), [cart, storeInfoCache, freshFreeShip]);

  // Items in the cart that are sold out RIGHT NOW. A cart item only remembers the stock it had when it was added, so a
  // piece that sold out (or that the seller set to 0) since then used to sit in the cart looking buyable until the very
  // last step. We compare against the live product list instead. Conservative on purpose — only flag when we are SURE:
  // the live row exists, the product has no advanced (per-size) stock, and the relevant stock number is a real 0 or less.
  const soldOutLive = useMemo(() => cart.filter(it => {
    const f = freshStock[it.id];
    if (!f || f.hasAdvanced) return false;
    const vn = it.selectedVariation?.name || it.selectedOption;
    if (vn && Array.isArray(f.variations)) {
      const v = f.variations.find(x => x && x.name === vn);
      if (v) return Number(v.stock ?? 1) <= 0;
    }
    if (Array.isArray(f.variations) && f.variations.length) return false;   // variation product with no matching variation on the line — let the server decide
    if (f.stock == null) return false;
    return Number(f.stock) <= 0;
  }), [cart, freshStock]);
  // Keep every bag line honest against the LIVE products: an option the product doesn't have is dropped (or the line removed),
  // and a wrong price is corrected — so a bag that picked up a stray option (or went stale) is repaired BEFORE the shopper
  // pays, instead of being charged a price the product never had. The server re-checks all of this at payment.
  const [cartFixes, setCartFixes] = useState([]);
  useEffect(() => {
    if (!Array.isArray(cart) || !cart.length || !Object.keys(freshStock).length) return;
    const rows = {};
    for (const [id, f] of Object.entries(freshStock)) rows[id] = { price: f.price, variations: f.variations, hasAdvanced: !!f.advContent };
    const { cart: next, notices } = reconcileCart(cart, rows);
    if (next !== cart) { setCart(next); if (notices.length) setCartFixes(prev => [...prev, ...notices.map(noticeText)]); }
  }, [freshStock, cart]); // eslint-disable-line react-hooks/exhaustive-deps
  const isSoldOutLive = (it) => soldOutLive.some(x => (x.cartKey || x.id) === (it.cartKey || it.id));
  const removeSoldOutLive = () => setCart(prev => prev.filter(it => !soldOutLive.some(x => (x.cartKey || x.id) === (it.cartKey || it.id))));

  // The store this cart belongs to. Normally the page-level store (from the public store list the app loads in the
  // background). But that list can be slow, blocked on some networks, or not loaded yet — and the cart used to show
  // "Store not found" and refuse to pay even though it was displaying the store's own info. A single-store cart now
  // resolves its store from its own items / the store info it already fetched, so checkout never depends on that list.
  const storeUser = useMemo(() => {
    if (storeUserProp && storeUserProp.id !== DEMO_ID) return storeUserProp;
    const ids = [...new Set(enrichedCart.map(i => i._storeInfo?.id || i.user_id).filter(Boolean))];
    if (ids.length === 1) {
      const info = enrichedCart.map(i => i._storeInfo).find(si => si && si.id === ids[0]) || storeInfoCache[ids[0]];
      if (info) return { ...info, id: ids[0] };
    }
    return storeUserProp;
  }, [storeUserProp, enrichedCart, storeInfoCache]);

  // Keep ref in sync so the beforeunload handler (which can't read state) has the latest items
  useEffect(() => { enrichedCartRef.current = enrichedCart; }, [enrichedCart]);

  // Stores in the cart that have paused orders (seller flipped it in Settings). Their
  // items stay visible but can't be checked out — the backend also hard-blocks this.
  const pausedStoreNames = [...new Set(
    enrichedCart.filter(i => i._storeInfo?.checkout_paused || i.checkout_paused).map(i => i._storeInfo?.store_name || "This store").filter(Boolean)
  )];
  const hasPausedStore = pausedStoreNames.length > 0;

  // ── Multi-store detection ───────────────────────────────────────────────────
  // Use user_id as the source of truth for store grouping — _storeInfo may not
  // be loaded yet if the customer navigated to cart quickly. Never fall back to
  // storeUser for items that have their own user_id (cross-store cart items).
  const uniqueStoreIds = [...new Set(enrichedCart.map(i => i._storeInfo?.id || i.user_id || storeUser?.id).filter(Boolean))];
  // Also treat as multi-store when ALL items are from a single store that is
  // DIFFERENT from storeUser (e.g. customer on MCXi's page but has a Studio
  // Sundar item) — prevents wrong store_id being stamped on the order.
  const isMultiStore = uniqueStoreIds.length > 1 ||
    (uniqueStoreIds.length === 1 &&
     storeUser?.id && storeUser.id !== DEMO_ID &&
     uniqueStoreIds[0] !== storeUser.id);
  // storeInfoLoading = true when any item has a user_id but _storeInfo not yet fetched
  const storeInfoLoading = enrichedCart.some(i => i.user_id && !i._storeInfo && !storeInfoCache[i.user_id]);

  // Group items by store for multi-store display & payment
  // Use _storeInfo?.id if available, else user_id as fallback key
  const storeGroups = isMultiStore
    ? enrichedCart.reduce((acc, item) => {
        const sid = item._storeInfo?.id || item.user_id;
        if (!sid) return acc;
        if (!acc[sid]) acc[sid] = { storeInfo: item._storeInfo || storeInfoCache[sid] || { id: sid }, items: [] };
        acc[sid].items.push(item);
        return acc;
      }, {})
    : null;
  const groupList = storeGroups ? Object.values(storeGroups) : null;

  // ── Financials ──────────────────────────────────────────────────────────────
  const subtotal = isMultiStore
    ? groupList.reduce((s, g) => s + g.items.reduce((ss, i) => ss + i.price * i.qty, 0), 0)
    : enrichedCart.reduce((s, i) => s + i.price * i.qty, 0);

  // International order (delivery country ≠ India): each store's FLAT intl_shipping_cost
  // applies instead of domestic shipping + free-ship thresholds. _storeInfo (from
  // store-info-bulk) is the authoritative source for the intl fields.
  const international = !!intlCountry && intlCountry !== "India";
  // A store's order ships FREE only when EVERY item in it is a free_shipping product. One
  // non-free item → the store's normal shipping (or intl flat) applies to the whole order.
  const allFreeShip = (items) => Array.isArray(items) && items.length > 0 && items.every(i => !!i.free_shipping);
  const shipOf = (info, gSub, gQty, items) => {
    if (allFreeShip(items)) return 0;
    if (international) return Number(info?.intl_shipping_cost || 0);
    // Per-state override: if the buyer picked a state the store set a rate for, that rate is
    // the base; otherwise the store's common shipping_cost. Free-ship thresholds still apply.
    const sm = info?.state_shipping;
    const stateBase = (deliveryState && sm && sm[deliveryState] != null) ? Number(sm[deliveryState]) : null;
    const base = stateBase != null ? stateBase : Number(info?.shipping_cost || 0);
    return effectiveShipping(base, gSub, gQty, info?.free_ship_min_amount, info?.free_ship_min_qty, info?.ship_surplus_after_qty, info?.ship_surplus_amount);
  };

  const shipping = isMultiStore
    ? groupList.reduce((s, g) => {
        const gSub = g.items.reduce((ss, i) => ss + i.price * i.qty, 0);
        const gQty = g.items.reduce((ss, i) => ss + (i.qty || 1), 0);
        return s + shipOf(g.storeInfo, gSub, gQty, g.items);
      }, 0)
    : shipOf(
        international ? (enrichedCart[0]?._storeInfo || storeUser) : storeUser,
        enrichedCart.reduce((s, i) => s + i.price * i.qty, 0),
        enrichedCart.reduce((s, i) => s + (i.qty || 1), 0),
        enrichedCart
      );

  // Resolver passed to AddressModal: for a chosen country it returns the intl shipping
  // total + which cart stores don't ship there (so the modal shows the fee + blocks it).
  const shippingResolver = (country) => {
    if (country === "India") return { international: false, shipping, unshippable: [] };
    const infos = isMultiStore ? groupList.map(g => g.storeInfo || {}) : [enrichedCart[0]?._storeInfo || storeUser || {}];
    const unshippable = infos.filter(s => !s.ships_international).map(s => s.store_name || "A store");
    const intlShip = infos.reduce((sum, s) => sum + Number(s.intl_shipping_cost || 0), 0);
    return { international: true, shipping: intlShip, unshippable };
  };

  // ── Bulk discount (applies per-store based on qty in that store's cart) ───
  // NOTE: computed BEFORE coupons on purpose — a coupon does NOT stack with an active
  // automatic offer (bundle/BOGO/bulk), so the coupon math below needs to know if this
  // store already has an offer discount.
  const getBulkDiscount = (storeInfo, items) => {
    // Offers never stack — the shopper gets whichever single deal is worth most.
    // Whatever this returns flows through total, storeFeeInputs and the discount
    // line automatically (they all read this one funnel).
    const bDisc  = bundleDiscount(storeInfo, items);
    const bgDisc = bogoDiscount(storeInfo, items);
    if (bgDisc > 0 && bgDisc >= bDisc) {
      const cfg = bogoConfig(storeInfo) || {};
      return { discount: bgDisc, rule: { bogo: true, buy: cfg.buy, get: cfg.get, label: bogoLabel(storeInfo), freeCount: bogoFreeCount(storeInfo, items) } };
    }
    if (bDisc > 0) {
      const bo = storeInfo.bundle_offer || {};
      return { discount: bDisc, rule: { bundle: true, min_qty: Number(bo.min_qty) || 0, price: Number(bo.price) || 0 } };
    }
    const rules = Array.isArray(storeInfo?.bulk_discounts) ? storeInfo.bulk_discounts.filter(d=>d.active!==false) : [];
    if (!rules.length) return { discount: 0, rule: null };
    const qty = items.reduce((s, i) => s + (i.qty || 1), 0);
    // Find the best (highest value) matching rule
    const matching = rules.filter(r => qty >= r.min_qty).sort((a,b) => b.min_qty - a.min_qty);
    if (!matching.length) return { discount: 0, rule: null };
    const best = matching[0];
    const storeSub = items.reduce((s, i) => s + i.price * i.qty, 0);
    const discount = best.type === "percent"
      ? Math.round(storeSub * best.value / 100)
      : Math.min(best.value, storeSub);
    return { discount, rule: best };
  };

  const bulkDiscountData = isMultiStore
    ? groupList.reduce((acc, g) => {
        const { discount, rule } = getBulkDiscount(g.storeInfo, g.items);
        acc.total += discount;
        if (rule) acc.byStore[g.storeInfo.id] = { discount, rule };
        return acc;
      }, { total: 0, byStore: {} })
    : (() => {
        const { discount, rule } = getBulkDiscount(storeUser, enrichedCart);
        return { total: discount, byStore: {}, rule };
      })();

  const bulkDiscount = bulkDiscountData.total;

  // Per-store coupon discount map (multi-store). A coupon does NOT stack with an active
  // automatic offer (bundle/BOGO/bulk): if this store already has an offer discount on the
  // order, its coupon contributes 0 (customer gets the single best deal, not both).
  const storeCouponDiscounts = useMemo(() => {
    if (!isMultiStore || !groupList) return {};
    const map = {};
    groupList.forEach(g => {
      const sc = storeCoupons[g.storeInfo.id]?.applied;
      if (!sc) { map[g.storeInfo.id] = 0; return; }
      if ((bulkDiscountData.byStore?.[g.storeInfo.id]?.discount || 0) > 0) { map[g.storeInfo.id] = 0; return; } // offer active → no stack
      const gSub = g.items.reduce((s, i) => s + i.price * i.qty, 0);
      map[g.storeInfo.id] = sc.discount_type === "percent"
        ? Math.round(gSub * sc.discount_value / 100)
        : Math.min(sc.discount_value, gSub);
    });
    return map;
  }, [isMultiStore, groupList, storeCoupons, bulkDiscountData]); // eslint-disable-line react-hooks/exhaustive-deps

  const couponDiscount = isMultiStore
    ? Object.values(storeCouponDiscounts).reduce((s, v) => s + v, 0)
    : (() => {
        if (!couponApplied) return 0;
        if (bulkDiscount > 0) return 0; // automatic offer active → coupon doesn't stack
        if (couponApplied.discount_type === "percent")
          return Math.round(subtotal * couponApplied.discount_value / 100);
        return Math.min(couponApplied.discount_value, subtotal);
      })();

  // Add-ons: price is added to the STORE's amount like shipping (not discounted, and the seller receives it in full).
  const addonsFor = (info) => resolveAddons(info?.checkout_addons, addonSel[info?.id]);
  const pickedSingle = isMultiStore ? [] : addonsFor(storeUser);
  const addonsSingleSum = addonsTotal(pickedSingle);
  // What we tell the server we charged, so it can re-check prices against each store's real settings.
  const addonsPayload = isMultiStore
    ? (groupList || []).flatMap(g => addonsFor(g.storeInfo).map(a => ({ storeId: g.storeInfo.id, id: a.id, price: a.price })))
    : pickedSingle.map(a => ({ storeId: storeUser?.id, id: a.id, price: a.price }));
  const total       = Math.max(0, subtotal + shipping + addonsSingleSum - couponDiscount - bulkDiscount);

  // Minimum order value — each store's merchandise subtotal (EXCLUDING shipping) must meet
  // its min_order_value. Blocks checkout with a clear "add ₹X more" until met (mirrors the
  // authoritative server gate in rzp-create-order).
  const minOrderIssues = (() => {
    const check = (info, sub) => {
      const min = Number(info?.min_order_value || 0);
      return (min > 0 && sub < min) ? { name: info?.store_name || "This store", min, short: min - sub } : null;
    };
    if (isMultiStore && groupList) {
      return groupList.map(g => check(g.storeInfo, g.items.reduce((s, i) => s + i.price * i.qty, 0))).filter(Boolean);
    }
    return [check(storeUser, subtotal)].filter(Boolean);
  })();
  const minOrderBlocked = minOrderIssues.length > 0;
  // Gross-up honoring each store's gateway_fee_payer. 'seller' → store absorbs the fee
  // (no customer markup; tsf=0 → pre-TSF → store nets amount×0.9746 via the existing payout
  // logic). Anything else → 'customer' (default = current behavior; customer pays ~2.4% on top).
  // Multi-store: compute per store so a mixed cart (one absorbs, one passes) is exact.
  // Per-store fee inputs — index 0 is the PRIMARY store (carries the platform fee + its
  // gross-up). Each store's `total` mirrors exactly what handlePay charges (shipping via the
  // SAME shipOf() the "Total Shipping" display uses — free-ship thresholds, per-item
  // free_shipping, state_shipping and intl all apply — plus per-store discounts) so the
  // displayed fee equals the charged fee. Previously used raw shipping_cost here, which
  // ignored those rules entirely: a store with an active free-ship threshold would show
  // "Free shipping" while still being charged for it underneath.
  const storeFeeInputs = useMemo(() => {
    if (isMultiStore && groupList) {
      return groupList.map(g => {
        const gSub    = g.items.reduce((s,i)=>s+i.price*i.qty,0);
        const gQty    = g.items.reduce((s,i)=>s+(i.qty||1),0);
        const gShip   = shipOf(g.storeInfo, gSub, gQty, g.items);
        const gCoupon = storeCouponDiscounts[g.storeInfo.id] || 0;
        const gBulk   = bulkDiscountData.byStore?.[g.storeInfo.id]?.discount || 0;
        return { total: Math.max(0, gSub + gShip + addonsTotal(addonsFor(g.storeInfo)) - gCoupon - gBulk),
                 storeId: g.storeInfo?.id, ...feeFlags(g.storeInfo) };
      });
    }
    return [{ total, storeId: storeUser?.id, ...feeFlags(storeUser) }];
  }, [isMultiStore, groupList, total, storeUser, storeCouponDiscounts, bulkDiscountData, international, deliveryState, addonSel]); // eslint-disable-line react-hooks/exhaustive-deps

  // MOP: null (disabled) reproduces today's exact behaviour — quote() falls through to
  // GST_FACTOR + the tiered platform %, unchanged. Only a real, enabled selection overrides it.
  const activeMethod = mop.enabled ? payMethod : null;
  // Gateway gross-up + tiered platform fee — mirrors iOS Fees.swift byte-for-byte (default path).
  const feeQuote     = useMemo(() => quote(storeFeeInputs, methodOpts(mop.methods, activeMethod)), [storeFeeInputs, mop.methods, activeMethod]);
  const grossAmount  = feeQuote.grossAmount;
  const txnFee       = feeQuote.gatewayFee;
  // Buyer-facing platform-fee line = ONLY the fee customer-pays stores add on top.
  // Absorb-store fees are borne by the seller and never shown to the buyer.
  const platformFee  = feeQuote.platformFeeCharged;
  // Precompute each method's total for the selector rows (always at today's default when MOP
  // is disabled — never rendered in that case — cheap: 3 extra quote() calls on cart totals).
  const mopMethodTotals = useMemo(() => {
    if (!mop.enabled) return null;
    const t = {};
    for (const key of Object.keys(mop.methods)) t[key] = quote(storeFeeInputs, methodOpts(mop.methods, key)).grossAmount;
    return t;
  }, [mop.enabled, mop.methods, storeFeeInputs]);

  // ── Coupon helpers (single-store only) ─────────────────────────────────────
  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true); setCouponError("");
    const allCoupons = Array.isArray(storeUser?.coupons) ? storeUser.coupons : [];
    const { coupon: c, reason } = findCoupon(allCoupons, couponCode, subtotal);
    setCouponLoading(false);
    if (!c) { setCouponError(reason === "expired" ? "This coupon has expired." : "Invalid or expired coupon code."); return; }
    if (c.min_order && subtotal < c.min_order) {
      setCouponError(`Minimum order ₹${c.min_order} required.`); return;
    }
    if (bulkDiscount > 0) {
      setCouponError("This coupon can't be combined with the current offer."); return;
    }
    setCouponApplied(c); setCouponError("");
  };
  const removeCoupon = () => { setCouponApplied(null); setCouponCode(""); setCouponError(""); };

  // Re-validate an APPLIED coupon whenever the cart changes. A coupon is checked at
  // apply-time, but if the customer then removes items and drops below the coupon's
  // min_order, it must not silently ride along to checkout (the BDAY30 min-₹600-on-₹400
  // bug). Auto-remove it and tell them. The server enforces this too (authoritative);
  // this keeps the UI honest so the shown total always matches what will be charged.
  useEffect(() => {
    if (isMultiStore || !couponApplied) return;
    const min = Number(couponApplied.min_order || 0);
    if (bulkDiscount > 0) {
      setCouponApplied(null);
      setCouponError("Coupon removed — it can't be combined with the current offer.");
    } else if (min > 0 && subtotal < min) {
      setCouponApplied(null);
      setCouponError(`Coupon removed — minimum order ₹${min} required (excluding shipping).`);
    }
  }, [subtotal, couponApplied, isMultiStore, bulkDiscount]);

  // Same re-validation for multi-store per-store coupons.
  useEffect(() => {
    if (!isMultiStore || !groupList) return;
    let changed = false;
    const next = { ...storeCoupons };
    groupList.forEach(g => {
      const sid = g.storeInfo.id;
      const applied = storeCoupons[sid]?.applied;
      if (!applied) return;
      const min = Number(applied.min_order || 0);
      const gSub = g.items.reduce((s, i) => s + i.price * i.qty, 0);
      if ((bulkDiscountData.byStore?.[sid]?.discount || 0) > 0) {
        next[sid] = { ...(storeCoupons[sid] || {}), applied: null, error: "Coupon removed — can't combine with this store's offer." };
        changed = true;
      } else if (min > 0 && gSub < min) {
        next[sid] = { ...(storeCoupons[sid] || {}), applied: null, error: `Coupon removed — min order ₹${min} required.` };
        changed = true;
      }
    });
    if (changed) setStoreCoupons(next);
  }, [isMultiStore, groupList, storeCoupons, bulkDiscountData]);

  // ── Per-store coupon helpers (multi-store) ──────────────────────────────────
  const getStoreCoupon = (sid) => storeCoupons[sid] || { code: "", applied: null, error: "", loading: false };
  const patchStoreCoupon = (sid, fields) => setStoreCoupons(prev => ({ ...prev, [sid]: { ...getStoreCoupon(sid), ...fields } }));
  const applyStoreCoupon = (sid, storeInfo, gSubtotal) => {
    const { code } = getStoreCoupon(sid);
    if (!code.trim()) return;
    patchStoreCoupon(sid, { loading: true, error: "" });
    const allCoupons = Array.isArray(storeInfo?.coupons) ? storeInfo.coupons : [];
    const { coupon: c, reason } = findCoupon(allCoupons, code, gSubtotal);
    if (!c) { patchStoreCoupon(sid, { loading: false, error: reason === "expired" ? "This coupon has expired." : "Invalid or expired coupon." }); return; }
    if (c.min_order && gSubtotal < c.min_order) {
      patchStoreCoupon(sid, { loading: false, error: `Min order ₹${c.min_order} required.` }); return;
    }
    if ((bulkDiscountData.byStore?.[sid]?.discount || 0) > 0) {
      patchStoreCoupon(sid, { loading: false, error: "Can't combine with this store's current offer." }); return;
    }
    patchStoreCoupon(sid, { loading: false, applied: c, error: "" });
  };
  const removeStoreCoupon = (sid) => patchStoreCoupon(sid, { applied: null, code: "", error: "" });

  // ── Item helpers ────────────────────────────────────────────────────────────
  const remove = (cartKey) => setCart(c => c.filter(x => (x.cartKey || x.id) !== cartKey));
  const updQ   = (cartKey, qty) => setCart(c => c.map(x => {
    if ((x.cartKey || x.id) !== cartKey) return x;
    const maxStock = x.selectedVariation ? Number(x.selectedVariation.stock ?? 1) : Number(x.stock ?? 1);
    return { ...x, qty: Math.min(Math.max(1, qty), maxStock) };
  }));

  // ── Pay flow ────────────────────────────────────────────────────────────────
  const requestPay = () => {
    if (soldOutLive.length) {          // never start a payment for something that is already gone
      setSoldOut({ list: soldOutLive.map(it => ({ id: it.id, name: it.name || "Item", variation: it.selectedVariation?.name || it.selectedOption || null, reason: "sold_out" })), retryable: false });
      return;
    }
    if (hasPausedStore) {
      alert(`${pausedStoreNames.join(", ")} ${pausedStoreNames.length > 1 ? "have" : "has"} paused orders right now. Please remove those items to check out.`);
      return;
    }
    if (minOrderBlocked) {
      const f = minOrderIssues[0];
      alert(`${f.name} has a minimum order of ₹${f.min} (excluding shipping). Add ₹${f.short} more to check out.`);
      return;
    }
    // Guest checkout — no login required. The address modal collects name/phone/email.
    if (!isMultiStore && (!storeUser || storeUser.id === DEMO_ID)) {
      alert(storeInfoLoading
        ? "Still loading this store's details — please wait a moment and tap Pay Now again."
        : "Store not found — please go back to the store page and try again.");
      return;
    }
    setShowAddr(true);
  };

  const handlePay = async (deliveryAddress, _coupon, contact) => {
    setShowAddr(false);
    lastPayArgsRef.current = { deliveryAddress, contact }; // remembered for sold-out retry
    // Guest checkout: prefer the contact entered in the address form; fall back to
    // the logged-in account. customer_id stays null for guests.
    const cName  = (contact?.name  || customer?.name  || "Customer").trim();
    const cPhone = (contact?.phone || customer?.phone || "").trim();
    const cEmail = (contact?.email || customer?.email || "").trim() || null;

    // Guard: if store info failed to load, stop — shipping costs and store
    // emails would be wrong, and items might be misrouted to the wrong store.
    if (storeInfoFetchFailed) {
      alert("Couldn't load store details — please refresh the page and try again.");
      setPaying(false);
      return;
    }
    // Guard: if store info is still loading for some items, don't proceed —
    // we'd risk creating a cross-store order with wrong store_id
    if (storeInfoLoading) {
      alert("Still loading store details — please try again in a moment.");
      setPaying(false);
      return;
    }
    // Guard: verify every cart item is covered by a store group (no orphans dropped).
    // If any item lost its sid somehow, it would silently disappear from the order.
    if (isMultiStore && storeGroups) {
      const coveredKeys = new Set(Object.values(storeGroups).flatMap(g => g.items.map(i => i.cartKey || i.id)));
      const orphans = enrichedCart.filter(i => !(coveredKeys.has(i.cartKey || i.id)));
      if (orphans.length > 0) {
        console.error("Cart orphan items detected:", orphans.map(i => i.name));
        alert("Some cart items couldn't be assigned to a store. Please refresh the page and try again.");
        setPaying(false);
        return;
      }
    }

    // public_stores exposes has_upi (not the raw upi_id); own/admin reads still carry upi_id.
    if (!isMultiStore && !(storeUser?.has_upi || storeUser?.upi_id)) {
      alert("This store hasn't set up payments yet. Please contact the store owner.");
      return;
    }
    setPaying(true);
    setPayStep("connecting");

    // Retry-release: if a previous attempt was dismissed (modal/tab closed) we left its
    // hold alive on purpose (UPI can complete out-of-band). Now that the customer is
    // starting a fresh attempt they're clearly abandoning the old one — release it so
    // they aren't blocked by their own stale hold. CAS-guarded backend = safe no-op if
    // that order was already paid/cancelled.
    if (pendingOrderIdsRef.current.length) {
      api.releaseStock(pendingOrderIdsRef.current);
      pendingOrderIdsRef.current = [];
    }

    // ── MULTI-STORE FLOW ────────────────────────────────────────────────────
    if (isMultiStore) {
      // Insert a pending order for each store
      const pendingOrders = await Promise.all(groupList.map(async (g, idx) => {
        const gSubtotal       = g.items.reduce((s, i) => s + i.price * i.qty, 0);
        const gQty            = g.items.reduce((s, i) => s + (i.qty || 1), 0);
        // Same shipOf() the "Total Shipping" display and storeFeeInputs use — NOT the raw
        // shipping_cost — so a free-ship threshold/per-item-free/state-shipping/intl rate
        // that waives or changes shipping is honored here too, not just in the total shown.
        const gShipping       = shipOf(g.storeInfo, gSubtotal, gQty, g.items);
        const gCouponDiscount = storeCouponDiscounts[g.storeInfo.id] || 0;
        const gCoupon         = storeCoupons[g.storeInfo.id]?.applied || null;
        const gBulkDiscount   = bulkDiscountData.byStore[g.storeInfo.id]?.discount || 0;
        const gBulkRule       = bulkDiscountData.byStore[g.storeInfo.id]?.rule || null;
        const gAddons         = addonsFor(g.storeInfo);
        const gTotal          = Math.max(0, gSubtotal + gShipping + addonsTotal(gAddons) - gCouponDiscount - gBulkDiscount);
        // Per-store tsf from the single fee quote (same store order as groupList). 'seller'
        // absorbs → tsf=0 (store nets gTotal×0.9746). Index 0 also carries the platform-fee
        // gross-up. Store payout is unchanged: tsf>0 → store is still paid the full amount.
        const gTsf      = feeQuote.perStoreTsf[idx];
        const orderId   = `BL-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 5).toUpperCase()}`;
        const pending   = {
          id: orderId, store_id: g.storeInfo.id,
          customer_name: cName,
          customer_phone: cPhone,
          customer_id: customer?.id || null,
          customer_email: cEmail,
          items: g.items, amount: feeQuote.perStoreAmount[idx], tsf: gTsf,
          ...(gAddons.length ? { addons: gAddons.map(({ id, name, price, image }) => ({ id, name, price, image })) } : {}),   // the server re-stamps these from the store's settings
          // Each store carries its OWN platform fee. Customer-pays: amount stays full, fee
          // is charged on top. Absorb: fee is taken OUT of amount (seller bears it). See fees.js.
          ...(feeQuote.perStorePlatformFee[idx] > 0 ? { platform_fee: feeQuote.perStorePlatformFee[idx] } : {}),
          gw_absorbed: !!feeQuote.perStoreGwAbsorbed[idx], pf_absorbed: !!feeQuote.perStorePfAbsorbed[idx], // settlement reads these
          status: "pending", proof: "", upi_ref: "", order_date: today(),
          delivery_address: deliveryAddress || null,
          customer_note: note.trim() || null,
          // Per-store applied coupon (each store's order records ITS own code + discount).
          ...(storeCoupons[g.storeInfo.id]?.applied?.code
            ? { coupon_code: storeCoupons[g.storeInfo.id].applied.code, coupon_discount: gCouponDiscount }
            : {}),
        };
        await api.insertOrder(pending);
        return { ...pending, storeInfo: g.storeInfo, gTotal, gShipping, gTsf, gCoupon, gCouponDiscount };
      }));
      pendingOrderIdsRef.current = pendingOrders.map(po => po.id); // track for beforeunload release

      // ── HOSTED redirect checkout (canary) — navigates the whole page to Razorpay ──
      if (isHostedPay()) {
        try { localStorage.setItem("bloom_pending_clear_cart", "1"); } catch { /* ignore */ }
        await startHostedRazorpayCheckout({
          amount: grossAmount,
          orderId: pendingOrders[0].id,
          orderIds: pendingOrders.map(po => po.id),
          storeId: "multi",
          storeName: "Bloom Marketplace",
          items: enrichedCart,
          coupon: null,
          feeInputs: storeFeeInputs, addons: addonsPayload, international, payMethod: activeMethod,
          customerNote: note.trim() || null,
          customerName:  cName,
          customerEmail: cEmail || "customer@bloom.store",
          customerPhone: cPhone || "9999999999",
          onError: (msg) => { setPaying(false); alert(msg); },
          onSoldOut: (list, info) => { setPaying(false); setSoldOut({ list, retryable: !!info?.retryable, message: info?.message }); },
        });
        return; // page is navigating to Razorpay's hosted checkout
      }

      await startRazorpayPayment({
        amount: grossAmount,
        orderId: pendingOrders[0].id,   // primary receipt ID for Razorpay notes
        storeId: pendingOrders[0].storeInfo.id,
        customerId: customer?.id || null,
        customerName:  cName,
        customerEmail: cEmail || "customer@bloom.store",
        customerPhone: cPhone || "9999999999",
        storeName: "Bloom Marketplace",
        storeSlug: "",
        storeEmail: pendingOrders[0].storeInfo.email,
        storePhone: pendingOrders[0].storeInfo.phone || "",
        ownerName:  pendingOrders[0].storeInfo.name,
        items: enrichedCart,
        deliveryAddress,
        shippingCost: shipping,
        coupon: null,
        feeInputs: storeFeeInputs, addons: addonsPayload, international, payMethod: activeMethod,
        customerNote: note.trim() || null,
        storeOrders: pendingOrders.map(po => ({
          bloomOrderId: po.id,
          storeId:      po.storeInfo.id,
          storeEmail:   po.storeInfo.email,
          storeName:    po.storeInfo.store_name,
          ownerName:    po.storeInfo.name,
          storePhone:   po.storeInfo.phone || "",
          items:        po.items,
          shippingCost: po.gShipping,
          storeAmount:  po.amount,        // per-store amount (absorb = total − platform fee)
          tsf:          po.gTsf,
          platformFee:  po.platform_fee || 0,
          coupon:       po.gCoupon || null,
          customerId:   customer?.id || null,
        })),
        onModalOpen: () => setPayStep("modal"),
        onVerifying: () => setPayStep("verifying"),
        onSuccess: async (pd) => {
          pendingOrderIdsRef.current = [];
          for (const po of pendingOrders) {
            onOrderPlaced(po.storeInfo.id, { ...po, status: "confirmed", upi_ref: String(pd?.paymentId || ""), date: today() });
          }
          clearCart ? clearCart() : setCart([]);   // hard-clear so the reload can't re-hydrate
          setPaying(false);
          setTimeout(() => window.location.reload(), 800);
        },
        onVerifyError: async () => {
          pendingOrderIdsRef.current = [];
          // Payment was captured by Razorpay but verify call failed — do NOT cancel the order.
          // The backend already confirmed it (or the webhook will). Just reload so it shows up.
          setPaying(false);
          alert("Your payment went through! Your order will appear in a moment.");
          setTimeout(() => window.location.reload(), 1000);
        },
        onFail: async (msg, opts = {}) => {
          const ids = pendingOrders.map(po => po.id);
          pendingOrderIdsRef.current = [];
          await Promise.all(ids.map(id => api.patchOrder(id, { status: "cancelled" })));
          // Release strictly by order id. Backend CAS-guards + reads items from the DB row,
          // so a never-reserved order (stockReserved:false / blocked checkout) is a safe no-op.
          if (opts.stockReserved !== false) api.releaseStock(ids);
          if (msg !== "Payment cancelled") alert(`Payment failed: ${msg}`);
          setPaying(false);
        },
        onDismiss: () => {
          // Modal closed without a result. Do NOT cancel/release — a UPI payment may
          // still land. Leave the orders pending + holds alive (they self-expire at the
          // 30-min TTL, or release when the customer retries). Keep pendingOrderIdsRef
          // so retry-release can find them. Just reset the UI.
          setPaying(false);
          setPayStep("connecting");
        },
      });
      return;
    }

    // ── SINGLE-STORE FLOW (unchanged) ────────────────────────────────────────
    const orderId = `BL-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 5).toUpperCase()}`;
    const pendingOrder = {
      id: orderId, store_id: storeUser.id,
      customer_name: cName,
      customer_phone: cPhone,
      customer_id: customer?.id || null,
          customer_email: cEmail,
      items: enrichedCart, amount: feeQuote.perStoreAmount[0], tsf: feeQuote.perStoreTsf[0],
      ...(pickedSingle.length ? { addons: pickedSingle.map(({ id, name, price, image }) => ({ id, name, price, image })) } : {}),   // re-stamped by the server from the store's settings
      // Platform fee: customer-pays → charged on top (amount stays full); absorb → taken
      // out of amount (seller bears it). Omitted when 0 (tier 0 / switch off).
      ...(feeQuote.perStorePlatformFee[0] > 0 ? { platform_fee: feeQuote.perStorePlatformFee[0] } : {}),
      gw_absorbed: !!feeQuote.perStoreGwAbsorbed[0], pf_absorbed: !!feeQuote.perStorePfAbsorbed[0], // settlement reads these
      status: "pending", proof: "", upi_ref: "", order_date: today(),
      delivery_address: deliveryAddress || null,
      customer_note: note.trim() || null,
      // Record the applied coupon so the seller can see it in the orders tab (informational).
      ...(couponApplied?.code ? { coupon_code: couponApplied.code, coupon_discount: couponDiscount } : {}),
    };
    const inserted = await api.insertOrder(pendingOrder);
    if (!inserted) {
      alert("Could not create order — please try again.");
      setPaying(false); return;
    }
    pendingOrderIdsRef.current = [orderId]; // track for beforeunload release

    // ── HOSTED redirect checkout (canary) — navigates the whole page to Razorpay ──
    if (isHostedPay()) {
      try { localStorage.setItem("bloom_pending_clear_cart", "1"); } catch { /* ignore */ }
      await startHostedRazorpayCheckout({
        amount: grossAmount, orderId, orderIds: [orderId],
        storeId: storeUser.id,
        storeName: storeUser.store_name,
        items: enrichedCart,
        coupon: couponApplied || null,
        feeInputs: storeFeeInputs, addons: addonsPayload, international, payMethod: activeMethod,
        customerNote: note.trim() || null,
        customerName:  cName,
        customerEmail: cEmail || "customer@bloom.store",
        customerPhone: cPhone || "9999999999",
        onError: (msg) => { setPaying(false); alert(msg); },
        onSoldOut: (list, info) => { setPaying(false); setSoldOut({ list, retryable: !!info?.retryable, message: info?.message }); },
      });
      return; // page is navigating to Razorpay's hosted checkout
    }

    await startRazorpayPayment({
      amount: grossAmount, orderId,
      storeId:   storeUser.id,
      customerId: customer?.id || null,
      customerName:  cName,
      customerEmail: cEmail || "customer@bloom.store",
      customerPhone: cPhone || "9999999999",
      storeName: storeUser.store_name, storeSlug: storeUser.store_slug,
      storeEmail: storeUser.email,
      storePhone: storeUser.phone || "",
      ownerName:  storeUser.name,
      items: enrichedCart,
      deliveryAddress,
      shippingCost: shipping,
      coupon: couponApplied || null,
      feeInputs: storeFeeInputs, addons: addonsPayload, international, payMethod: activeMethod,
      bulkDiscount: bulkDiscount > 0 ? { amount: bulkDiscount, rule: bulkDiscountData.rule } : null,
      customerNote: note.trim() || null,
      onModalOpen: () => setPayStep("modal"),
      onVerifying: () => setPayStep("verifying"),
      onSuccess: async (pd) => {
        pendingOrderIdsRef.current = [];
        const confirmedOrder = { ...pendingOrder, status: "confirmed", upi_ref: String(pd?.paymentId || "") };
        onOrderPlaced(storeUser.id, { ...confirmedOrder, date: today() });
        clearCart ? clearCart() : setCart([]);   // hard-clear so the reload can't re-hydrate
        setPaying(false);
        setTimeout(() => window.location.reload(), 800);
      },
      onVerifyError: async () => {
        pendingOrderIdsRef.current = [];
        // Payment was captured by Razorpay but verify call failed — do NOT cancel the order.
        // The backend already confirmed it (or the webhook will). Just reload so it shows up.
        setPaying(false);
        alert("Your payment went through! Your order will appear in a moment.");
        setTimeout(() => window.location.reload(), 1000);
      },
      onFail: async (msg, opts = {}) => {
        pendingOrderIdsRef.current = [];
        await api.patchOrder(orderId, { status: "cancelled" });
        // Release strictly by order id. Backend CAS-guards + reads items from the DB row,
        // so a never-reserved order (stockReserved:false / blocked checkout) is a safe no-op.
        if (opts.stockReserved !== false) api.releaseStock(orderId);
        if (msg !== "Payment cancelled") alert(`Payment failed: ${msg}`);
        setPaying(false);
      },
      onDismiss: () => {
        // Modal closed without a result. Do NOT cancel/release — a UPI payment may still
        // land. Leave the order pending + hold alive (self-expires at the 30-min TTL, or
        // releases when the customer retries). Keep pendingOrderIdsRef for retry-release.
        setPaying(false);
        setPayStep("connecting");
      },
    });
  };

  // Re-pay after removeSoldOutAndContinue trims the cart (fires once the new cart state
  // lands). MUST stay ABOVE the early return below — it's a hook, so it has to run on
  // every render, including the empty-cart path (otherwise React crashes the page).
  useEffect(() => {
    if (!retryAfterCartChange.current) return;
    retryAfterCartChange.current = false;
    const a = lastPayArgsRef.current;
    if (cart.length && a) handlePay(a.deliveryAddress, null, a.contact);
  }, [cart]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!cart.length) return (
    <div style={{ padding:"72px 6vw", textAlign:"center" }}>
      <div style={{ fontSize:56 }}><BloomMark /></div>
      <h2 style={{ fontSize:32, marginTop:16 }}>Your cart is empty</h2>
      <p style={{ color:"var(--ink-soft)", marginTop:8 }}>Browse stores and add something lovely.</p>
    </div>
  );

  // For ThemeWrapper: use single-store template only; multi-store uses no template
  const templateForWrapper = isMultiStore ? undefined : storeUser?.template;

  // Drop the sold-out items from the cart, then re-run checkout with the trimmed cart.
  // We flip a ref and let the effect below fire after the cart state actually updates,
  // so handlePay reads fresh state (avoids a stale-cart race).
  const removeSoldOutAndContinue = () => {
    const soldKeys = new Set((soldOut?.list || [])
      .filter(u => u.reason === "sold_out")
      .map(u => `${u.id}|${u.variation || ""}`));
    const next = cart.filter(it =>
      !soldKeys.has(`${it.id}|${it.selectedVariation?.name || it.selectedOption || ""}`));
    setSoldOut(null);
    retryAfterCartChange.current = true;
    setCart(next);
  };
  // Transient verify failure (DB blip) — just retry the same cart, no removal.
  const retrySamePay = () => {
    const a = lastPayArgsRef.current;
    setSoldOut(null);
    if (a) handlePay(a.deliveryAddress, null, a.contact);
  };
  return (
    <ThemeWrapper template={templateForWrapper}>
    <div style={{ padding:isMobile?"20px 16px":"44px 6vw", maxWidth:960, margin:"0 auto" }}>
      {showAddr && <AddressModal customer={customer} onConfirm={handlePay} onClose={() => setShowAddr(false)} shippingResolver={shippingResolver} onCountryChange={setIntlCountry} onStateChange={setDeliveryState}/>}

      {soldOut && (
        <div style={{ position:"fixed", inset:0, background:"var(--overlay)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:420, padding:24 }}>
          <div style={{ background:"var(--white)", borderRadius:4, padding:"24px 22px", maxWidth:380, width:"100%", boxShadow:"0 20px 60px rgba(0,0,0,0.35)" }}>
            <div style={{ fontSize:24, marginBottom:8 }}><Icon n="warning" size={24} inline /></div>
            {soldOut.retryable ? (<>
              <h3 style={{ margin:"0 0 6px", fontFamily:"Syne", fontWeight:800, fontSize:17 }}>Couldn’t verify stock</h3>
              <p style={{ margin:"0 0 18px", color:"var(--text2)", fontSize:13.5, lineHeight:1.5 }}>{soldOut.message || "Something interrupted the stock check. Please try again."}</p>
              <button onClick={retrySamePay} style={{ width:"100%", padding:"13px", borderRadius:4, border:"none", background:"var(--teal)", color:"var(--on-accent)", fontWeight:700, fontSize:15, cursor:"pointer", marginBottom:10 }}>Try again</button>
            </>) : (<>
              <h3 style={{ margin:"0 0 6px", fontFamily:"Syne", fontWeight:800, fontSize:17 }}>
                {soldOut.list.length === 1 ? "This item just sold out" : `${soldOut.list.length} items just sold out`}
              </h3>
              <p style={{ margin:"0 0 12px", color:"var(--text2)", fontSize:13.5, lineHeight:1.5 }}>No longer available — remove to continue with the rest of your order:</p>
              <div style={{ background:"var(--red-light)", border:"1px solid #fecaca", borderRadius:4, padding:"10px 12px", marginBottom:18 }}>
                {soldOut.list.map((u,i) => (
                  <div key={i} style={{ fontSize:13.5, color:"var(--red)", fontWeight:600, padding:"3px 0" }}>
                    • {u.name}{u.variation ? ` (${u.variation})` : ""}
                  </div>
                ))}
              </div>
              <button onClick={removeSoldOutAndContinue} style={{ width:"100%", padding:"13px", borderRadius:4, border:"none", background:"var(--teal)", color:"var(--on-accent)", fontWeight:700, fontSize:15, cursor:"pointer", marginBottom:10 }}>Remove these &amp; continue</button>
            </>)}
            <button onClick={() => setSoldOut(null)} style={{ width:"100%", padding:"11px", borderRadius:4, border:"1px solid var(--border)", background:"var(--white)", color:"var(--text2)", fontWeight:600, fontSize:14, cursor:"pointer" }}>Back to cart</button>
          </div>
        </div>
      )}

      {paying && (
        <div style={{ position:"fixed", inset:0, background:"var(--overlay)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", zIndex:400, gap:16, padding:24 }}>
          <div style={{ width:52, height:52, border:"4px solid rgba(255,255,255,0.15)", borderTopColor:"#2F63DD", borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
          {payStep === "connecting" && <>
            <p style={{ color:"#fff", fontFamily:"Syne", fontWeight:700, fontSize:16, margin:0 }}>Setting up payment…</p>
            <p style={{ color:"rgba(255,255,255,0.45)", fontFamily:"Syne", fontSize:12, margin:0, textAlign:"center" }}>Connecting to payment gateway</p>
          </>}
          {payStep === "modal" && <>
            <p style={{ color:"#fff", fontFamily:"Syne", fontWeight:700, fontSize:16, margin:0 }}>Complete payment in your UPI app</p>
            <p style={{ color:"rgba(255,255,255,0.45)", fontFamily:"Syne", fontSize:12, margin:0, textAlign:"center" }}>Approve the request in PhonePe / GPay / Paytm — then come back here</p>
          </>}
          {payStep === "verifying" && <>
            <p style={{ color:"#fff", fontFamily:"Syne", fontWeight:700, fontSize:16, margin:0 }}>Confirming your payment…</p>
            <p style={{ color:"rgba(255,255,255,0.45)", fontFamily:"Syne", fontSize:12, margin:0, textAlign:"center" }}>Please wait, do not close this page</p>
          </>}
        </div>
      )}

      <h2 style={{ fontSize:isMobile?28:40, marginBottom:isMobile?20:36 }}>Your <em>Cart</em></h2>
      {cartFixes.length > 0 && (
        <div role="status" style={{ background:"var(--warn-bg)", border:"1px solid var(--warn-bd)", color:"var(--warn-fg)", borderRadius:4, padding:"12px 14px", marginBottom:16, fontSize:13, lineHeight:1.5 }}>
          <strong>We updated your bag</strong>
          {cartFixes.map((m, i) => <div key={i} style={{ marginTop:4 }}>{m}</div>)}
          <button onClick={() => setCartFixes([])} style={{ marginTop:8, background:"none", border:"none", padding:0, color:"var(--warn-fg)", fontWeight:700, cursor:"pointer", fontSize:12, textDecoration:"underline" }}>Got it</button>
        </div>
      )}

      {customer && (
        <div style={{ background:"var(--green-bg)", borderRadius:4, padding:"12px 18px", marginBottom:20, display:"flex", alignItems:"center", gap:10, fontSize:13 }}>
          <span style={{ fontSize:18 }}><BloomMark /></span>
          <span>Logged in as <strong>{customer.name}</strong></span>
        </div>
      )}

      {isMultiStore && (
        <div style={{ background:"var(--teal-light)", border:"1px solid var(--teal-mid)", borderRadius:4, padding:"12px 18px", marginBottom:20, fontSize:13, color:"var(--ink-soft)" }}>
          {groupList.length === 1
            ? <><Icon n="bag" size="1.1em" inline /> From <strong>{groupList[0].storeInfo.store_name}</strong>{shipping > 0 ? <> · <strong>₹{shipping} shipping</strong></> : " · Free shipping"}</>
            : <><Icon n="bag" size="1.1em" inline /> <strong>{groupList.length} stores</strong>: {groupList.map(g => g.storeInfo.store_name).join(", ")} · one payment, shipped separately · <strong>₹{shipping} total shipping</strong></>
          }
        </div>
      )}

      {!isMultiStore && enrichedCart.length > 0 && enrichedCart[0]._storeInfo && (
        <div style={{ background:"var(--teal-light)", border:"1px solid var(--teal-mid)", borderRadius:4, padding:"12px 18px", marginBottom:20, fontSize:13, color:"var(--ink-soft)" }}>
          <Icon n="bag" size="1.1em" inline /> From <strong>{enrichedCart[0]._storeInfo.store_name}</strong>{shipping > 0 ? <> · <strong>₹{shipping} shipping</strong></> : " · Free shipping"}
        </div>
      )}

      <div style={{ display:"flex", flexDirection:isMobile?"column":"row", gap:20, alignItems:"flex-start" }}>
        {/* ── Items ── */}
        <div style={{ flex:1, minWidth:0, display:"flex", flexDirection:"column", gap:14 }}>

          {isMultiStore ? (
            // Multi-store: grouped by store
            groupList.map((g) => (
              <div key={g.storeInfo.id}>
                {/* Store header */}
                <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 0 6px", borderBottom:"1px solid var(--border)", marginBottom:10 }}>
                  {g.storeInfo.profile_image && <img src={thumbUrl(g.storeInfo.profile_image, 60)} style={{ width:22, height:22, borderRadius:"50%", objectFit:"cover" }} alt=""/>}
                  <span style={{ fontWeight:700, fontSize:13 }}>{g.storeInfo.store_name}</span>
                  {Number(g.storeInfo.shipping_cost) > 0 && (
                    <span style={{ fontSize:11, color:"var(--ink-soft)", marginLeft:"auto" }}>+₹{g.storeInfo.shipping_cost} shipping</span>
                  )}
                </div>
                {g.items.map(item => {
                  const ck = item.cartKey || item.id;
                  const varName = item.selectedVariation?.name || item.selectedOption || item.selectedCombo || null;
                  return (
                    <div key={ck} className="card fadeUp" style={{ padding:isMobile?12:16, display:"flex", flexDirection:isMobile?"column":"row", gap:isMobile?8:12, alignItems:isMobile?"stretch":"center", marginBottom:10 }}>
                      <div style={{ display:"flex", gap:12, alignItems:"center", flex:isMobile?undefined:1, minWidth:isMobile?undefined:0 }}>
                        <img src={thumbUrl((Array.isArray(item.images) && item.images[0]) || item.img, 200)} alt={item.name} style={{ width:isMobile?52:68, height:isMobile?52:68, objectFit:"cover", borderRadius:4, flexShrink:0 }}/>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontWeight:600, fontSize:isMobile?13:14, whiteSpace:isMobile?"normal":"nowrap", overflow:isMobile?"visible":"hidden", textOverflow:isMobile?"unset":"ellipsis" }}>{item.name}</div>
                          {varName && <div style={{ fontSize:11, fontWeight:700, color:"var(--teal-deep)", background:"var(--teal-light)", borderRadius:4, padding:"2px 8px", display:"inline-block", marginTop:3 }}>{varName}</div>}
                          {item.custom_answer && (
                            <div style={{ marginTop:5, background:"var(--warn-bg)", borderRadius:4, padding:"5px 9px", borderLeft:"2.5px solid #F59E0B" }}>
                              {item.custom_question && <div style={{ fontSize:10, fontWeight:700, color:"var(--warn-fg)", marginBottom:2 }}>{item.custom_question}</div>}
                              <div style={{ fontSize:12, color:"var(--warn-fg)", lineHeight:1.5 }}>{item.custom_answer}</div>
                            </div>
                          )}
                          {item.custom_image && (
                            <div style={{ marginTop:5, background:"var(--info-bg)", borderRadius:4, padding:"5px 9px", borderLeft:"2.5px solid #3B82F6", display:"flex", alignItems:"center", gap:8 }}>
                              <img src={item.custom_image} alt="Your upload" style={{ width:34, height:34, objectFit:"cover", borderRadius:4, border:"1px solid var(--info-bd)" }}/>
                              <span style={{ fontSize:10, fontWeight:700, color:"var(--info-fg)" }}><Icon n="camera" size="1.1em" inline /> Your uploaded image</span>
                            </div>
                          )}
                          <div style={{ color:"var(--teal)", fontSize:isMobile?15:17, fontWeight:700, marginTop:2 }}>₹{(Number(item.price)||0).toLocaleString()}</div>
                          {isSoldOutLive(item) && <div style={{ color:"var(--red)", fontSize:12, fontWeight:700, marginTop:3 }}><Icon n="blocked" size="1.1em" inline /> Sold out — remove it to check out</div>}
                          {isMobile && <button onClick={() => remove(ck)} style={{ background:"none", border:"none", cursor:"pointer", padding:"4px 0", marginTop:2, display:"block" }}><Icon n="trash" size={14} color="#C05"/></button>}
                        </div>
                      </div>
                      {(()=>{ const maxStock=item.selectedVariation?Number(item.selectedVariation.stock??1):Number(item.stock??1); return (
                        <div style={{ display:"flex", alignItems:"center", gap:6, justifyContent:isMobile?"flex-start":"flex-start" }}>
                          <button onClick={() => { item.qty<=1 ? remove(ck) : updQ(ck, item.qty-1); }} style={{ background:"var(--teal-light)", border:"none", width:36, height:36, borderRadius:4, cursor:"pointer", color:"var(--teal-deep)", fontWeight:700 }}>−</button>
                          <span style={{ fontWeight:700, fontSize:14, minWidth:16, textAlign:"center" }}>{item.qty}</span>
                          <button onClick={() => updQ(ck, item.qty+1)} disabled={item.qty>=maxStock} style={{ background:item.qty>=maxStock?"var(--surface)":"var(--teal-light)", border:"none", width:36, height:36, borderRadius:4, cursor:item.qty>=maxStock?"not-allowed":"pointer", color:item.qty>=maxStock?"var(--ink-soft)":"var(--teal-deep)", fontWeight:700, opacity:item.qty>=maxStock?0.4:1 }}>+</button>
                          {!isMobile && <button onClick={() => remove(ck)} style={{ background:"none", border:"none", cursor:"pointer", padding:4, marginLeft:4 }}><Icon n="trash" size={14} color="#C05"/></button>}
                        </div>
                      ); })()}
                    </div>
                  );
                })}
                {/* Per-store coupon input */}
                {(() => {
                  const sid = g.storeInfo.id;
                  const sc  = getStoreCoupon(sid);
                  const gSub = g.items.reduce((s, i) => s + i.price * i.qty, 0);
                  const hasCoupons = Array.isArray(g.storeInfo?.coupons) && g.storeInfo.coupons.some(c => c.active);
                  if (!hasCoupons) return null;
                  return (
                    <div style={{ marginTop:6, marginBottom:4 }}>
                      {!sc.applied ? (
                        <div>
                          <div style={{ display:"flex", gap:6 }}>
                            <input className="inp" placeholder="Coupon code"
                              value={sc.code}
                              onChange={e => patchStoreCoupon(sid, { code: e.target.value.toUpperCase() })}
                              onKeyDown={e => e.key === "Enter" && applyStoreCoupon(sid, g.storeInfo, gSub)}
                              style={{ flex:1, padding:"7px 12px", fontSize:12 }}/>
                            <button onClick={() => applyStoreCoupon(sid, g.storeInfo, gSub)} disabled={sc.loading}
                              style={{ background:"var(--pink-btn)", color:"var(--on-accent)", border:"none", borderRadius:4, padding:"7px 12px", fontSize:11, fontWeight:700, cursor:"pointer", flexShrink:0 }}>
                              {sc.loading ? "…" : "Apply"}
                            </button>
                          </div>
                          {sc.error && <p style={{ fontSize:11, color:"var(--red)", marginTop:4 }}>{sc.error}</p>}
                        </div>
                      ) : (
                        <div style={{ background:"var(--green-bg)", borderRadius:4, padding:"8px 12px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                          <div>
                            <span style={{ fontSize:12, fontWeight:700, color:"var(--green)" }}><Icon n="success" size={12} inline /> {sc.applied.code}</span>
                            <span style={{ fontSize:11, color:"var(--green)", marginLeft:8 }}>
                              -{sc.applied.discount_type === "percent" ? `${sc.applied.discount_value}%` : `₹${sc.applied.discount_value}`} off
                            </span>
                          </div>
                          <button onClick={() => removeStoreCoupon(sid)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:16, lineHeight:1 }}>×</button>
                        </div>
                      )}
                    </div>
                  );
                })()}
                <AddonPicker addons={activeAddons(g.storeInfo?.checkout_addons)} selectedIds={addonSel[g.storeInfo.id] || []} onToggle={(id) => toggleAddon(g.storeInfo.id, id)} note={`Optional extras from ${g.storeInfo.store_name}`}/>
              </div>
            ))
          ) : (
            // Single-store: flat list
            enrichedCart.map(item => {
              const ck = item.cartKey || item.id;
              const varName = item.selectedVariation?.name || item.selectedOption || item.selectedCombo || null;
              return (
                <div key={ck} className="card fadeUp" style={{ padding:isMobile?14:18, display:"flex", flexDirection:isMobile?"column":"row", gap:isMobile?8:12, alignItems:isMobile?"stretch":"center" }}>
                  <div style={{ display:"flex", gap:12, alignItems:"center", flex:isMobile?undefined:1, minWidth:isMobile?undefined:0 }}>
                    <img src={thumbUrl((Array.isArray(item.images) && item.images[0]) || item.img, 200)} alt={item.name} style={{ width:isMobile?56:76, height:isMobile?56:76, objectFit:"cover", borderRadius:4, flexShrink:0 }}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:600, fontSize:isMobile?13:15, whiteSpace:isMobile?"normal":"nowrap", overflow:isMobile?"visible":"hidden", textOverflow:isMobile?"unset":"ellipsis" }}>{item.name}</div>
                      {varName && <div style={{ fontSize:11, fontWeight:700, color:"var(--teal-deep)", background:"var(--teal-light)", borderRadius:4, padding:"2px 8px", display:"inline-block", marginTop:3 }}>{varName}</div>}
                      <div style={{ color:"var(--teal)", fontSize:isMobile?16:18, fontWeight:700, marginTop:2 }}>₹{(Number(item.price)||0).toLocaleString()}</div>
                      {isSoldOutLive(item) && <div style={{ color:"var(--red)", fontSize:12, fontWeight:700, marginTop:3 }}><Icon n="blocked" size="1.1em" inline /> Sold out — remove it to check out</div>}
                      {isMobile && <button onClick={() => remove(ck)} style={{ background:"none", border:"none", cursor:"pointer", padding:"4px 0", marginTop:2, display:"block" }}><Icon n="trash" size={15} color="#C05"/></button>}
                    </div>
                  </div>
                  {(()=>{ const maxStock=item.selectedVariation?Number(item.selectedVariation.stock??1):Number(item.stock??1); return (
                    <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                      <button onClick={() => { item.qty<=1 ? remove(ck) : updQ(ck, item.qty-1); }} style={{ background:"var(--teal-light)", border:"none", width:36, height:36, borderRadius:4, cursor:"pointer", color:"var(--teal-deep)", fontWeight:700 }}>−</button>
                      <span style={{ fontWeight:700, fontSize:14, minWidth:16, textAlign:"center" }}>{item.qty}</span>
                      <button onClick={() => updQ(ck, item.qty+1)} disabled={item.qty>=maxStock} style={{ background:item.qty>=maxStock?"var(--surface)":"var(--teal-light)", border:"none", width:36, height:36, borderRadius:4, cursor:item.qty>=maxStock?"not-allowed":"pointer", color:item.qty>=maxStock?"var(--ink-soft)":"var(--teal-deep)", fontWeight:700, opacity:item.qty>=maxStock?0.4:1 }}>+</button>
                      {!isMobile && <div style={{ fontWeight:700, fontSize:14, minWidth:60, textAlign:"right" }}>₹{(item.price*item.qty).toLocaleString()}</div>}
                      {!isMobile && <button onClick={() => remove(ck)} style={{ background:"none", border:"none", cursor:"pointer", padding:4, marginLeft:4 }}><Icon n="trash" size={15} color="#C05"/></button>}
                    </div>
                  ); })()}
                </div>
              );
            })
          )}
          {!isMultiStore && <AddonPicker addons={activeAddons(storeUser?.checkout_addons)} selectedIds={addonSel[storeUser?.id] || []} onToggle={(id) => toggleAddon(storeUser?.id, id)}/>}
        </div>

        {/* ── Order Summary ── */}
        <div className="card" style={{ padding:24, width:isMobile?"100%":320, flexShrink:0, position:isMobile?"static":"sticky", top:80 }}>
          <h3 style={{ fontSize:20, marginBottom:18 }}>Order Summary</h3>

          {isMultiStore ? (
            // Multi-store summary: per-store breakdown with explicit shipping + 1 shared TSF
            <>
              {groupList.map((g, gi) => {
                const gSub  = g.items.reduce((s, i) => s + i.price * i.qty, 0);
                const gQty  = g.items.reduce((s, i) => s + (i.qty || 1), 0);
                const gShip = shipOf(g.storeInfo, gSub, gQty, g.items);   // same rule as the charge (thresholds, state, surplus)
                return (
                  <div key={g.storeInfo.id} style={{ marginBottom:gi < groupList.length-1 ? 14 : 6 }}>
                    {/* Store label */}
                    <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:6, padding:"5px 10px", background:"var(--surface)", borderRadius:4 }}>
                      {g.storeInfo.profile_image && <img src={thumbUrl(g.storeInfo.profile_image, 60)} style={{ width:18, height:18, borderRadius:"50%", objectFit:"cover", flexShrink:0 }} alt=""/>}
                      <span style={{ fontSize:12, fontWeight:700, color:"var(--ink)" }}><Icon n="store" size={12} inline /> {g.storeInfo.store_name}</span>
                      <span style={{ fontSize:11, color:"var(--ink-soft)", marginLeft:"auto" }}>₹{gSub.toLocaleString()}</span>
                    </div>
                    {/* Items */}
                    {g.items.map(i => {
                      const vn = i.selectedVariation?.name || i.selectedOption || i.selectedCombo || null;
                      return (
                        <div key={i.cartKey || i.id} style={{ display:"flex", justifyContent:"space-between", marginBottom:4, fontSize:12, color:"var(--ink-soft)", paddingLeft:4 }}>
                          <span>{i.name}{vn ? ` (${vn})` : ""} ×{i.qty}</span>
                          <span style={{ color:"var(--ink)", fontWeight:600 }}>₹{(i.price * i.qty).toLocaleString()}</span>
                        </div>
                      );
                    })}
                    {/* Per-store shipping */}
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, paddingLeft:4, marginTop:2 }}>
                      <span style={{ color:"var(--ink-soft)" }}><Icon n="package" size="1.1em" inline /> Shipping from {g.storeInfo.store_name}</span>
                      <span style={{ fontWeight:600, color: gShip > 0 ? "var(--ink)" : "var(--green)" }}>
                        {gShip > 0 ? `₹${gShip.toLocaleString()}` : "Free"}
                      </span>
                    </div>
                    {addonsFor(g.storeInfo).map(a => (
                      <div key={a.id} data-addon-line style={{ display:"flex", justifyContent:"space-between", fontSize:12, paddingLeft:4, marginTop:2 }}>
                        <span style={{ color:"var(--ink-soft)" }}><Icon n="gift" size="1.1em" inline /> {a.name}</span>
                        <span style={{ fontWeight:600 }}>{a.price > 0 ? `₹${a.price.toLocaleString()}` : "Free"}</span>
                      </div>
                    ))}
                    {/* Per-store coupon discount */}
                    {storeCouponDiscounts[g.storeInfo.id] > 0 && (() => {
                      const sc = storeCoupons[g.storeInfo.id]?.applied;
                      return (
                        <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, paddingLeft:4, marginTop:2 }}>
                          <span style={{ color:"var(--green)" }}><Icon n="success" size="1.1em" inline /> {sc?.code} coupon</span>
                          <span style={{ fontWeight:600, color:"var(--green)" }}>−₹{storeCouponDiscounts[g.storeInfo.id].toLocaleString()}</span>
                        </div>
                      );
                    })()}
                    {gi < groupList.length - 1 && <div style={{ height:1, background:"var(--border)", margin:"10px 0 0" }}/>}
                  </div>
                );
              })}
              {/* Combined shipping line */}
              {groupList.length > 1 && (
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginTop:6, padding:"6px 0", borderTop:"1px dashed var(--border)" }}>
                  <span style={{ color:"var(--ink-soft)", fontWeight:600 }}>Total Shipping ({groupList.length} stores)</span>
                  <span style={{ fontWeight:700, color:"var(--ink)" }}>₹{shipping.toLocaleString()}</span>
                </div>
              )}
            </>
          ) : (
            // Single-store summary
            <>
              {/* Store header for single-store cart */}
              {enrichedCart.length > 0 && enrichedCart[0]._storeInfo && (
                <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:12, padding:"8px 10px", background:"var(--surface)", borderRadius:4 }}>
                  {enrichedCart[0]._storeInfo.profile_image && <img src={enrichedCart[0]._storeInfo.profile_image} style={{ width:18, height:18, borderRadius:"50%", objectFit:"cover", flexShrink:0 }} alt=""/>}
                  <span style={{ fontSize:12, fontWeight:700, color:"var(--ink)" }}><Icon n="store" size={12} inline /> {enrichedCart[0]._storeInfo.store_name}</span>
                  <span style={{ fontSize:11, color:"var(--ink-soft)", marginLeft:"auto" }}><Icon n="location" size={11} inline /></span>
                </div>
              )}
              {enrichedCart.map(i => {
                const vn = i.selectedVariation?.name || i.selectedOption || i.selectedCombo || null;
                return (
                  <div key={i.cartKey || i.id} style={{ display:"flex", justifyContent:"space-between", marginBottom:8, fontSize:13, color:"var(--ink-soft)" }}>
                    <span>{i.name}{vn ? ` (${vn})` : ""} × {i.qty}</span>
                    <span style={{ color:"var(--ink)", fontWeight:600 }}>₹{(i.price * i.qty).toLocaleString()}</span>
                  </div>
                );
              })}
              {shipping > 0 && (
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8, fontSize:13, color:"var(--ink-soft)" }}>
                  <span>Shipping</span>
                  <span style={{ color:"var(--ink)", fontWeight:600 }}>₹{shipping.toLocaleString()}</span>
                </div>
              )}
              {pickedSingle.map(a => (
                <div key={a.id} data-addon-line style={{ display:"flex", justifyContent:"space-between", marginBottom:8, fontSize:13, color:"var(--ink-soft)" }}>
                  <span><Icon n="gift" size="1.1em" inline /> {a.name}</span>
                  <span style={{ color:"var(--ink)", fontWeight:600 }}>{a.price > 0 ? `₹${a.price.toLocaleString()}` : "Free"}</span>
                </div>
              ))}
              {/* Coupon (single-store only) */}
              {!couponApplied ? (
                <div style={{ marginTop:12, marginBottom:8 }}>
                  <div style={{ display:"flex", gap:7 }}>
                    <input className="inp" placeholder="Coupon code" value={couponCode}
                      onChange={e => setCouponCode(e.target.value.toUpperCase())}
                      onKeyDown={e => e.key === "Enter" && applyCoupon()}
                      style={{ flex:1, padding:"9px 14px", fontSize:13 }}/>
                    <button onClick={applyCoupon} disabled={couponLoading}
                      style={{ background:"var(--pink-btn)", color:"var(--on-accent)", border:"none", borderRadius:4, padding:"9px 14px", fontSize:12, fontWeight:700, cursor:"pointer", flexShrink:0 }}>
                      {couponLoading ? "…" : "Apply"}
                    </button>
                  </div>
                  {couponError && <p style={{ fontSize:11, color:"var(--red)", marginTop:5 }}>{couponError}</p>}
                </div>
              ) : (
                <div style={{ background:"var(--green-bg)", borderRadius:4, padding:"10px 14px", marginTop:12, marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div>
                    <span style={{ fontSize:12, fontWeight:700, color:"var(--green)" }}><Icon n="success" size={12} inline /> {couponApplied.code}</span>
                    <span style={{ fontSize:11, color:"var(--green)", marginLeft:8 }}>
                      -{couponApplied.discount_type === "percent" ? `${couponApplied.discount_value}%` : `₹${couponApplied.discount_value}`} off
                    </span>
                  </div>
                  <button onClick={removeCoupon} style={{ background:"none", border:"none", cursor:"pointer", fontSize:16, lineHeight:1 }}>×</button>
                </div>
              )}
              {couponDiscount > 0 && (
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4, fontSize:13 }}>
                  <span style={{ color:"var(--green)", fontWeight:600 }}>Coupon Discount</span>
                  <span style={{ color:"var(--green)", fontWeight:700 }}>−₹{couponDiscount.toLocaleString()}</span>
                </div>
              )}
              {bulkDiscount > 0 && (
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4, fontSize:13 }}>
                  <span style={{ color:"#f59e0b", fontWeight:600 }}><Icon n="gift" size="1.1em" inline /> {
                    bulkDiscountData.rule?.bogo
                      ? `${bulkDiscountData.rule.label}${bulkDiscountData.rule.freeCount ? ` · ${bulkDiscountData.rule.freeCount} free` : ""}`
                      : bulkDiscountData.rule?.bundle
                        ? `${bulkDiscountData.rule.min_qty} for ₹${Number(bulkDiscountData.rule.price).toLocaleString()}`
                        : "Bulk Discount"
                  }</span>
                  <span style={{ color:"#f59e0b", fontWeight:700 }}>−₹{bulkDiscount.toLocaleString()}</span>
                </div>
              )}
              {/* BOGO nudge — "add 1 more eligible item to get 1 free" */}
              {storeUser?.bogo_offer?.active && (() => {
                const needed = bogoUnitsToNext(storeUser, enrichedCart);
                if (!(needed > 0)) return null;
                const cfg = bogoConfig(storeUser);
                return (
                  <div style={{background:"var(--warn-bg)",border:"1px solid #f59e0b",borderRadius:4,padding:"8px 12px",marginTop:8,fontSize:12,color:"var(--warn-fg)"}}>
                    <Icon n="bag" size="1.1em" inline /> Add {needed} more eligible item{needed>1?"s":""} to get <strong>{cfg.get} free</strong> ({bogoLabel(storeUser)})
                  </div>
                );
              })()}
              {/* Bundle nudge — encourage reaching the "N for ₹X" deal */}
              {bulkDiscount === 0 && storeUser?.bundle_offer?.active && (() => {
                const bo = storeUser.bundle_offer;
                const totalQty = enrichedCart.reduce((s,i)=>s+(i.qty||1),0);
                const needed = Number(bo.min_qty||0) - totalQty;
                if (!(needed > 0)) return null;
                return (
                  <div style={{background:"var(--warn-bg)",border:"1px solid #f59e0b",borderRadius:4,padding:"8px 12px",marginTop:8,fontSize:12,color:"var(--warn-fg)"}}>
                    <Icon n="gift" size="1.1em" inline /> Add {needed} more item{needed>1?"s":""} to get <strong>{bo.min_qty} for ₹{Number(bo.price).toLocaleString()}</strong>!
                  </div>
                );
              })()}
              {/* Show bulk discount nudge if customer is close to a tier */}
              {bulkDiscount === 0 && !storeUser?.bundle_offer?.active && (() => {
                const rules = Array.isArray(storeUser?.bulk_discounts) ? storeUser.bulk_discounts.filter(d=>d.active!==false).sort((a,b)=>a.min_qty-b.min_qty) : [];
                const totalQty = enrichedCart.reduce((s,i)=>s+(i.qty||1),0);
                const next = rules.find(r=>r.min_qty > totalQty);
                if (!next) return null;
                const needed = next.min_qty - totalQty;
                return (
                  <div style={{background:"var(--warn-bg)",border:"1px solid #f59e0b",borderRadius:4,padding:"8px 12px",marginTop:8,fontSize:12,color:"var(--warn-fg)"}}>
                    <Icon n="gift" size="1.1em" inline /> Add {needed} more item{needed>1?"s":""} to get {next.type==="percent"?`${next.value}% off`:`₹${next.value} off`}!
                  </div>
                );
              })()}
            </>
          )}

          {/* MOP (Mode-of-Payment) selector — only shown when the feature is switched on for
              this viewer (admin-wide, or the ?mop=1 preview flag). UPI pre-selected. Deliberately
              shows NO price on the buttons themselves — just the method name — so there is only
              ONE number the customer ever sees per state: the "You Pay" grand total below, which
              already reacts to the selection via feeQuote → methodOpts(mop.methods, activeMethod).
              mopMethodTotals still backs that grand total; it's just never rendered per-button. */}
          {mop.enabled && mopMethodTotals && (
            <div style={{ borderTop:"1px solid var(--border)", paddingTop:14, marginTop:8 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"var(--ink-soft)", marginBottom:8 }}>Payment method</div>
              <div style={{ display:"flex", flexDirection: isMobile ? "column" : "row", gap:8 }}>
                {Object.entries(mop.methods).map(([key, m]) => {
                  const selected = payMethod === key;
                  return (
                    <button key={key} type="button" onClick={() => setPayMethod(key)}
                      style={{
                        flex:1, textAlign:"left", cursor:"pointer", padding:"10px 12px", borderRadius:4,
                        border: selected ? "2px solid var(--pink-btn)" : "1px solid var(--border)",
                        background: selected ? "rgba(47,99,221,0.06)" : "var(--surface)",
                        fontFamily:"inherit",
                      }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8 }}>
                        <span style={{ fontWeight:700, fontSize:13, color:"var(--ink)" }}>{m.label}</span>
                        {selected && <span style={{ color:"var(--pink-btn)", fontSize:13 }}><Icon n="done" size={11} strokeWidth={2.5} /></span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ borderTop:"1px solid var(--border)", paddingTop:14, marginTop:8 }}>
            {/* Collapsed by default — keep the focus on the final amount, not the fee itemization. */}
            <button type="button" onClick={() => setShowBreakdown(v => !v)}
              style={{ background:"none", border:"none", cursor:"pointer", color:"var(--ink-soft)", fontSize:12, fontWeight:600, padding:0, display:"flex", alignItems:"center", gap:5, marginBottom: showBreakdown ? 10 : 12 }}>
              {showBreakdown ? "Hide price breakdown ▲" : "View price breakdown ▾"}
            </button>
            {showBreakdown && (
              <div style={{ marginBottom:12 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8, fontSize:13, color:"var(--ink-soft)" }}>
                  <span>Cart total{international ? " (incl. shipping)" : ""}</span>
                  <span style={{ fontWeight:600, color:"var(--ink)" }}>₹{total.toLocaleString()}</span>
                </div>
                {/* Fees intentionally rendered small + muted — transparent, but not the headline. */}
                {txnFee > 0 && (
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:5, fontSize:11, color:"var(--ink-soft)", opacity:0.72 }}>
                    {/* "Card processing fee" — never "Razorpay fee": Razorpay's real cost on most
                        cards is far lower than international; this line is Bloom's own card
                        rate, not a pass-through of Razorpay's actual charge. */}
                    <span>{activeMethod === "card" ? "Card processing fee" : `Transaction fee (${isMultiStore && groupList.length > 1 ? "1 shared · " : ""}payment gateway)`}</span>
                    <span>₹{txnFee.toLocaleString()}</span>
                  </div>
                )}
                {platformFee > 0 && (
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:11, color:"var(--ink-soft)", opacity:0.72 }}>
                    <span>Bloom platform fee</span>
                    <span>₹{platformFee.toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}
            <div style={{ borderTop:"1px dashed var(--border)", paddingTop:10, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ fontWeight:700, fontSize:16 }}>You Pay</span>
              <span style={{ color:"var(--pink-btn)", fontSize:24, fontWeight:700 }}>₹{grossAmount.toLocaleString()}</span>
            </div>
          </div>
          {/* Note to store */}
          <div style={{ marginTop:14, paddingTop:14, borderTop:"1px solid var(--border)" }}>
            <label style={{ display:"block", fontSize:11, fontWeight:700, color:"var(--ink-soft)", marginBottom:6 }}>
              <Icon n="chat" size="1.1em" inline /> Note to store (optional)
            </label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value.slice(0, 300))}
              placeholder="E.g. gift wrap please, leave at door, any special instructions…"
              rows={2}
              style={{ width:"100%", padding:"9px 12px", border:"1.5px solid var(--border)", borderRadius:4, fontSize:12, fontFamily:"inherit", resize:"vertical", lineHeight:1.5, boxSizing:"border-box", outline:"none", transition:"border-color 0.18s" }}
              onFocus={e => e.target.style.borderColor = "var(--teal)"}
              onBlur={e => e.target.style.borderColor = "var(--border)"}
            />
            {note.length > 0 && <div style={{ fontSize:10, color:"var(--ink-soft)", textAlign:"right", marginTop:2 }}>{note.length}/300</div>}
          </div>

          <div style={{ marginTop:14 }}>
            {paymentsPaused ? (
              <>
                <button disabled style={{ width:"100%", padding:15, fontSize:14, borderRadius:4, border:"none", background:"rgba(0,0,0,0.08)", color:"var(--ink-soft)", cursor:"not-allowed", fontWeight:600 }}>
                  <Icon n="locked" size="1.1em" inline /> Payments Paused
                </button>
                <p style={{ fontSize:12, color:"var(--ink-soft)", textAlign:"center", marginTop:8, lineHeight:1.5 }}>
                  Your cart is saved — we'll be accepting orders again soon! <BloomMark />
                </p>
              </>
            ) : (
              <>
                <button className="btn-green" style={{ width:"100%", padding:15, fontSize:14, borderRadius:4, opacity: (hasPausedStore || minOrderBlocked || soldOutLive.length) ? 0.5 : 1, cursor: (hasPausedStore || minOrderBlocked || soldOutLive.length) ? "not-allowed" : undefined }} onClick={requestPay} disabled={paying || hasPausedStore || minOrderBlocked || soldOutLive.length > 0}>
                  {soldOutLive.length ? <><Icon n="blocked" size="1.1em" inline />{" Sold-out item in cart"}</> : hasPausedStore ? <><Icon n="locked" size="1.1em" inline />{" Paused store in cart"}</> : minOrderBlocked ? `Add ₹${minOrderIssues[0].short} more to check out` : paying ? "Opening payment…" : <><Icon n="card" size="1.1em" inline />{" Pay Now"}</>}
                </button>
                {soldOutLive.length
                  ? <p style={{ fontSize:12, color:"var(--red)", textAlign:"center", marginTop:8, fontWeight:600 }}><Icon n="blocked" size={12} inline /> {soldOutLive.map(i => i.name).join(", ")} {soldOutLive.length > 1 ? "are" : "is"} sold out. <button onClick={removeSoldOutLive} style={{ background:"none", border:"none", color:"var(--red)", textDecoration:"underline", fontWeight:700, cursor:"pointer", padding:0, fontSize:12 }}>Remove {soldOutLive.length > 1 ? "them" : "it"}</button> to check out.</p>
                  : hasPausedStore
                  ? <p style={{ fontSize:12, color:"var(--red)", textAlign:"center", marginTop:8, fontWeight:600 }}><Icon n="pause" size={12} inline /> {pausedStoreNames.join(", ")} {pausedStoreNames.length > 1 ? "have" : "has"} paused orders. Remove {pausedStoreNames.length > 1 ? "those" : "that store's"} items to check out.</p>
                  : minOrderBlocked
                  ? <p style={{ fontSize:12, color:"var(--red)", textAlign:"center", marginTop:8, fontWeight:600 }}><Icon n="bag" size={12} inline /> {minOrderIssues[0].name} has a minimum order of ₹{minOrderIssues[0].min} (excluding shipping){minOrderIssues.length > 1 ? " — and others in your cart" : ""}.</p>
                  : <p style={{ fontSize:11, color:"var(--ink-soft)", textAlign:"center", marginTop:8 }}>UPI · Cards · Wallets · Secured by Razorpay</p>}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
    </ThemeWrapper>
  );
}
